import React, { useEffect, useMemo, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';

import { PACKAGE_STATUS_APPROVED } from '~/consts/package';

import { useGetOfferStatuses } from '~/hooks/apis/offers/useOfferStatuses';
import { RentalImportStatusItem } from '~/hooks/apis/vendors/useBulkUploadRentals';

import StatCard from './StatCard';

type RowFilter = 'all' | 'queued' | 'importing' | 'imported' | 'published' | 'failed';

const IMPORT_STATUS_TO_FILTER: Record<string, RowFilter> = {
  queued: 'queued',
  importing: 'importing',
  imported: 'imported',
  failed_import: 'failed',
  format_error: 'failed',
  database_error: 'failed',
};

const STATUS_LABEL: Record<RowFilter, string> = {
  all: 'All',
  queued: 'Queued',
  importing: 'Importing',
  imported: 'Imported',
  published: 'Published',
  failed: 'Failed import',
};

const STATUS_COLOR: Record<RowFilter, 'default' | 'primary' | 'info' | 'success' | 'error'> = {
  all: 'default',
  queued: 'default',
  importing: 'primary',
  imported: 'info',
  published: 'success',
  failed: 'error',
};

interface Props {
  status: RentalImportStatusItem;
  onRetry: (rowNumber: number) => void;
  retryingRowNumber?: number;
  onBack: () => void;
  onClose: () => void;
  onRequestClose?: (requestClose: () => void) => void;
}

export default function BulkUploadRentalsTracker({
  status,
  onRetry,
  retryingRowNumber,
  onBack,
  onClose,
  onRequestClose,
}: Props) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RowFilter>('all');
  const [confirmingClose, setConfirmingClose] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const batchInProgress = status.state !== 'completed' && status.state !== 'failed';

  const handleCloseClick = () => {
    setConfirmingClose(true);
  };

  // Let the parent Dialog's backdrop/Escape close trigger this same confirmation, instead of being blocked outright.
  useEffect(() => {
    onRequestClose?.(handleCloseClick);
  }, [onRequestClose]);

  const importedOfferIds = useMemo(
    () => status.results.filter((row) => row.import_status === 'imported' && row.offer_id).map((row) => row.offer_id),
    [status.results],
  );

  const {
    data: offerStatuses,
    refetch: refetchOfferStatuses,
    isFetching: isFetchingOfferStatuses,
  } = useGetOfferStatuses(importedOfferIds);

  const rowsWithState = useMemo(() => {
    return status.results.map((row) => {
      let state: RowFilter = IMPORT_STATUS_TO_FILTER[row.import_status ?? ''] ?? 'queued';
      if (state === 'imported' && row.offer_id && offerStatuses?.[row.offer_id] === PACKAGE_STATUS_APPROVED) {
        state = 'published';
      }
      return { id: row.row_number, ...row, state };
    });
  }, [status.results, offerStatuses]);

  const counts = useMemo(() => {
    const c = { queued: 0, importing: 0, imported: 0, published: 0, failed: 0 };
    rowsWithState.forEach(({ state }) => {
      if (state !== 'all') {
        c[state]++;
      }
    });
    return c;
  }, [rowsWithState]);

  const processedCount = counts.imported + counts.published + counts.failed;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rowsWithState.filter((row) => {
      if (filter !== 'all' && row.state !== filter) {
        return false;
      }
      if (q && !row.hotel_code?.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rowsWithState, filter, search]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filter, search]);

  const filterDefs: Array<{ key: RowFilter; count: number }> = [
    { key: 'all', count: status.total_rows },
    { key: 'queued', count: counts.queued },
    { key: 'importing', count: counts.importing },
    { key: 'imported', count: counts.imported },
    { key: 'published', count: counts.published },
    { key: 'failed', count: counts.failed },
  ];

  const columns: Array<GridColDef> = [
    { field: 'vendor_id', headerName: 'Vendor Id', flex: 1, minWidth: 110 },
    { field: 'offer_id', headerName: 'Offer Id', flex: 1, minWidth: 110 },
    { field: 'hotel_code', headerName: 'Hotel Code', flex: 1, minWidth: 120 },
    {
      field: 'state',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const state: RowFilter = params.value;
        return (
          <Chip
            label={STATUS_LABEL[state]}
            color={STATUS_COLOR[state]}
            size="small"
            icon={state === 'importing' ? <CircularProgress size={12} /> : undefined}
          />
        );
      },
    },
    {
      field: 'errors',
      headerName: 'Message',
      flex: 1.5,
      minWidth: 200,
      valueGetter: (_value, row) => row.errors?.join(', ') ?? '',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 110,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (params.row.import_status === 'failed_import') {
          const isRetryingThisRow = retryingRowNumber === params.row.row_number;
          return (
            <Button size="small" disabled={isRetryingThisRow} onClick={() => onRetry(params.row.row_number)}>
              {isRetryingThisRow ? 'Retrying…' : 'Retry'}
            </Button>
          );
        }
        if (params.row.state === 'imported' || params.row.state === 'published') {
          return (
            <Button size="small" href={`/offers/${params.row.offer_id}`} target="_blank" rel="noopener">
              Review
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Box>
            <CopyableField
              value={status.batch_id}
              label={`Batch – ${status.batch_id}`}
              tooltip="Copy batch ID"
              variant="subtitle1"
              fontWeight={700}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {processedCount} of {status.total_rows} rows processed. Keep the batch ID to check on this import later
              &ndash; it's only available for 24 hours after upload.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ visibility: batchInProgress ? 'visible' : 'hidden' }}
            >
              This may take a while. The import will keep running in the background even if you close this dialog.
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <StatCard label="Queued" count={counts.queued} color="text.primary" />
            <StatCard label="Importing" count={counts.importing} color="primary.main" />
            <StatCard label="Imported" count={counts.imported} color="info.main" />
            <StatCard label="Published" count={counts.published} color="success.main" />
            <StatCard label="Failed" count={counts.failed} color="error.main" />
          </Stack>

          <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: 'grey.200',
              }}
            >
              <Tabs
                value={filter}
                onChange={(_event, value) => setFilter(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 32, minWidth: 0, '& .MuiTab-root': { minHeight: 32, py: 0.5 } }}
              >
                {filterDefs.map((f) => (
                  <Tab key={f.key} value={f.key} label={`${STATUS_LABEL[f.key]} (${f.count})`} />
                ))}
              </Tabs>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                disabled={batchInProgress || importedOfferIds.length === 0 || isFetchingOfferStatuses}
                onClick={() => refetchOfferStatuses()}
              >
                {isFetchingOfferStatuses ? 'Refreshing…' : 'Refresh statuses'}
              </Button>
              <TextField
                size="small"
                placeholder="Search by hotel code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}
              />
            </Box>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowHeight={() => 62}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnSelector
              disableColumnMenu
              autoHeight
              rowHeight={62}
              sx={{ border: 0, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
            />
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button variant="text" onClick={handleCloseClick}>
          Close
        </Button>
      </DialogActions>

      <Dialog open={confirmingClose} onClose={() => setConfirmingClose(false)}>
        <DialogTitle>Close bulk upload?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {batchInProgress
              ? `The import is still in progress. It will keep running in the background — copy the batch ID (${status.batch_id}) if you want to check on it later. Batches are kept for 24 hours.`
              : `You can look this batch up again later using its ID: ${status.batch_id}. Batches are kept for 24 hours.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmingClose(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
